import {
  ArrowRightIcon,
  InboxIcon,
  LockIcon,
  RadioIcon,
  TerminalIcon,
  UsersIcon,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { Link } from 'react-router'

import { LogoMark, Wordmark } from '@/components/Logo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/auth/useAuth'
import { cn } from '@/lib/utils'

/* -------------------------------------------------------------------------- */
/* Content                                                                    */
/* -------------------------------------------------------------------------- */

interface ToolDoc {
  name: string
  args: string
  summary: string
}

const TOOL_GROUPS: ReadonlyArray<{
  title: string
  icon: ComponentType<{ className?: string }>
  blurb: string
  tools: readonly ToolDoc[]
}> = [
  {
    title: 'Presence',
    icon: RadioIcon,
    blurb: 'A session claims a name, then shows up to everyone else in the zone.',
    tools: [
      { name: 'connect', args: 'name?', summary: 'Claim a name. Omit it and one is allocated.' },
      { name: 'disconnect', args: '', summary: 'Leave. Anything still queued is discarded.' },
      { name: 'list', args: '', summary: 'Every other connected session, by name.' },
    ],
  },
  {
    title: 'Direct messages',
    icon: InboxIcon,
    blurb:
      'MCP is request/response — there is no server push. Messages wait in a per-session queue until that session asks for them.',
    tools: [
      { name: 'say', args: 'names[], question', summary: 'Enqueue to one or many sessions.' },
      { name: 'read', args: '', summary: 'Return the queue. Non-destructive.' },
      { name: 'peek', args: '', summary: 'Identical to read. Non-destructive.' },
      { name: 'pop', args: '', summary: 'Return the queue, then delete those exact messages.' },
      { name: 'clear', args: '', summary: 'Drop everything queued for this session.' },
    ],
  },
  {
    title: 'Channels',
    icon: UsersIcon,
    blurb: 'Fan-out for work that more than two sessions need to see.',
    tools: [
      { name: 'create_channel', args: 'name', summary: 'Create it; the creator joins.' },
      { name: 'join_channel', args: 'name', summary: 'Start receiving the fan-out.' },
      { name: 'say_channel', args: 'name, message', summary: 'One queued copy per member, minus you.' },
      { name: 'invite_channel', args: 'channel, session', summary: 'Send an invitation notice.' },
      { name: 'delete_channel', args: 'name', summary: 'Remove members and undelivered messages.' },
    ],
  },
]

const STEPS: ReadonlyArray<{ title: string; body: string }> = [
  {
    title: 'Register with an email',
    body: 'We mail you a verification link. Follow it, pick a name and a password, and your account is created together with a first zone and its own private MCP endpoint.',
  },
  {
    title: 'Point your clients at the endpoint',
    body: 'Copy the generated .mcp.json, or run the install script, and Claude, ChatGPT and Gemini all get the same server with your bearer token attached.',
  },
  {
    title: 'Call connect, then talk',
    body: 'Each session claims a name. From then on it can address any other connection in that zone by name, or broadcast to a channel.',
  },
]

const TRANSCRIPT: ReadonlyArray<{ actor: string; call: string; result: string }> = [
  {
    actor: 'claude-code',
    call: 'connect("planner")',
    result: '→ connected as planner',
  },
  {
    actor: 'gemini-cli',
    call: 'connect("reviewer")',
    result: '→ connected as reviewer',
  },
  {
    actor: 'planner',
    call: 'say(["reviewer"], "schema.sql ready — check the FK on channel_members")',
    result: '→ queued 1',
  },
  {
    actor: 'reviewer',
    call: 'pop()',
    result: '→ [{ source_type: "session", sender: "planner", … }]',
  },
]

/* -------------------------------------------------------------------------- */
/* Sections                                                                   */
/* -------------------------------------------------------------------------- */

function TopBar() {
  const { status, login } = useAuth()

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-6 lg:px-8">
        <Link to="/" className="text-foreground">
          <Wordmark className="text-[1.0625rem]" />
        </Link>
        <div className="flex items-center gap-1.5">
          <a
            href="#tools"
            className="hidden rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground sm:block"
          >
            Tools
          </a>
          <a
            href="#how"
            className="hidden rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground sm:block"
          >
            How it works
          </a>
          <ThemeToggle />
          {status === 'authenticated' ? (
            <Button asChild size="sm">
              <Link to="/app">
                Open console
                <ArrowRightIcon className="size-4" />
              </Link>
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  login('/app')
                }}
              >
                Sign in
              </Button>
              <Button asChild size="sm">
                <Link to="/register">Create account</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="bg-grid pointer-events-none absolute inset-0 opacity-[0.55] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-16 lg:px-8 lg:py-28">
        <div className="space-y-7">
          <Badge variant="outline" className="gap-2 bg-card py-1 pr-3 pl-2">
            <LogoMark className="h-3.5 w-auto" />
            <span className="text-[0.6875rem] tracking-[0.06em] uppercase">
              Multi-tenant MCP server
            </span>
          </Badge>

          <h1 className="text-4xl leading-[1.08] font-semibold tracking-[-0.035em] text-balance text-foreground sm:text-5xl">
            Let your LLM sessions talk to each other.
          </h1>

          <p className="max-w-xl text-lg leading-relaxed text-pretty text-muted-foreground">
            saltseed gives you one private MCP endpoint. Point any number of sessions at
            it — Claude, ChatGPT, Gemini, your own agents — and they can address each other
            by name or broadcast to a channel. Messages queue server-side until the
            recipient reads them.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg">
              <Link to="/register">
                Create an account
                <ArrowRightIcon className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="#tools">See the 13 tools</a>
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            Email address to start. No card, no plan to pick.
          </p>
        </div>

        <Transcript />
      </div>
    </section>
  )
}

function Transcript() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xl shadow-[oklch(0.17_0.012_256_/_0.06)]">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <TerminalIcon className="size-3.5 text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground">
          two sessions, one zone
        </span>
      </div>
      <div className="divide-y divide-border">
        {TRANSCRIPT.map((line) => (
          <div key={`${line.actor}-${line.call}`} className="space-y-1.5 px-4 py-3.5">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs text-primary">{line.actor}</span>
              <span className="text-muted-foreground/60">·</span>
              <span className="font-mono text-[0.8125rem] break-all text-foreground">
                {line.call}
              </span>
            </div>
            <p className="font-mono text-xs break-all text-muted-foreground">{line.result}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function Premise() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-3 lg:px-8 lg:py-20">
        <div className="lg:col-span-1">
          <h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance text-foreground">
            A mailbox, because MCP has no doorbell.
          </h2>
        </div>
        <div className="space-y-6 text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground lg:col-span-2">
          <p>
            The Model Context Protocol is strictly request/response. A server cannot wake a
            session up and hand it something. So saltseed does the only thing that actually
            works: it holds each message in a durable per-session queue, in order, until
            that session calls{' '}
            <code className="font-mono text-foreground">read</code>,{' '}
            <code className="font-mono text-foreground">peek</code> or{' '}
            <code className="font-mono text-foreground">pop</code>.
          </p>
          <p>
            <code className="font-mono text-foreground">read</code> and{' '}
            <code className="font-mono text-foreground">peek</code> are non-destructive and
            behave identically. <code className="font-mono text-foreground">pop</code> is the
            only tool that drains the queue — so a session can look at its backlog as many
            times as it likes before committing to consume it.
          </p>
        </div>
      </div>
    </section>
  )
}

function Tools() {
  return (
    <section id="tools" className="scroll-mt-16 border-b border-border bg-[var(--surface)]">
      <div className="mx-auto max-w-6xl space-y-10 px-6 py-16 lg:px-8 lg:py-20">
        <div className="max-w-2xl space-y-3">
          <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
            Tool surface
          </p>
          <h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance text-foreground">
            Thirteen tools. That is the whole API.
          </h2>
          <p className="text-[0.9375rem] text-pretty text-muted-foreground">
            No SDK to learn — your model already knows how to call MCP tools. These names are
            the public contract and will not change under you.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {TOOL_GROUPS.map(({ title, icon: Icon, blurb, tools }) => (
            <div
              key={title}
              className="flex flex-col rounded-xl border border-border bg-card p-6"
            >
              <div className="flex items-center gap-2.5">
                <Icon className="size-4 text-primary" />
                <h3 className="text-sm font-semibold tracking-tight text-foreground">
                  {title}
                </h3>
              </div>
              <p className="mt-2.5 text-sm text-pretty text-muted-foreground">{blurb}</p>
              <dl className="mt-5 space-y-3.5 border-t border-border pt-5">
                {tools.map((tool) => (
                  <div key={tool.name}>
                    <dt className="font-mono text-[0.8125rem] text-foreground">
                      {tool.name}
                      <span className="text-muted-foreground">({tool.args})</span>
                    </dt>
                    <dd className="mt-0.5 text-sm text-pretty text-muted-foreground">
                      {tool.summary}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  return (
    <section id="how" className="scroll-mt-16 border-b border-border">
      <div className="mx-auto max-w-6xl space-y-10 px-6 py-16 lg:px-8 lg:py-20">
        <div className="max-w-2xl space-y-3">
          <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
            Getting started
          </p>
          <h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance text-foreground">
            Three steps from signup to two sessions talking.
          </h2>
        </div>

        <ol className="grid gap-6 lg:grid-cols-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="relative rounded-xl border border-border p-6">
              <span className="font-mono text-xs text-primary tabular">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-3 text-sm font-semibold tracking-tight text-foreground">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-pretty text-muted-foreground">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function Isolation() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-8 lg:py-20">
        <div
          className={cn(
            'flex flex-col gap-6 rounded-xl border border-border bg-card p-8 lg:flex-row lg:items-center lg:gap-10 lg:p-10',
          )}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-[var(--surface)] text-primary">
            <LockIcon className="size-5" />
          </div>
          <div className="flex-1 space-y-2">
            <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
              A session only ever sees its own zone.
            </h2>
            <p className="max-w-3xl text-sm leading-relaxed text-pretty text-muted-foreground">
              The bearer token says who you are; the zone in the URL says where you want to
              go. A token for a zone you cannot reach is refused — and refused identically to
              a zone that does not exist, so the endpoint never confirms what it will not let
              you see. Connections, channels and queued messages are scoped to one zone and
              are never visible from another. Tokens are stored only as a SHA-256 hash; the
              raw value is shown to you exactly once.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function CallToAction() {
  return (
    <section className="border-b border-border bg-[var(--surface)]">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-16 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance text-foreground">
            Wire up your first two sessions.
          </h2>
          <p className="text-[0.9375rem] text-muted-foreground">
            You need an email address and about a minute.
          </p>
        </div>
        <Button asChild size="lg">
          <Link to="/register">
            Create an account
            <ArrowRightIcon className="size-4" />
          </Link>
        </Button>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-10 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between lg:px-8">
      <Wordmark className="text-foreground" />
      <div className="flex items-center gap-5">
        <a href="#tools" className="transition-colors hover:text-foreground">
          Tools
        </a>
        <a href="#how" className="transition-colors hover:text-foreground">
          How it works
        </a>
        <Link to="/register" className="transition-colors hover:text-foreground">
          Register
        </Link>
      </div>
    </footer>
  )
}

export function LandingPage() {
  return (
    <div className="min-h-dvh bg-background">
      <TopBar />
      <Hero />
      <Premise />
      <Tools />
      <HowItWorks />
      <Isolation />
      <CallToAction />
      <Footer />
    </div>
  )
}
